import type { AppLocale } from '@lineage/core';
import { create } from 'zustand';
import { en, type Messages } from './en';

export type { Messages } from './en';

/** English is built in; other languages are separate chunks, loaded when chosen. */
const loaders: Record<AppLocale, () => Promise<Messages>> = {
  en: () => Promise.resolve(en),
  es: () => import('./es').then((m) => m.es),
};

/** The language on screen now. Changes only once its text has loaded, so a page never mixes two languages. */
export const useI18n = create<{ locale: AppLocale; t: Messages }>(() => ({ locale: 'en', t: en }));

let languageListener: ((locale: AppLocale) => void) | undefined;

/** Called with each new language (used to tell Firebase which language to send emails in). */
export function onLanguageChange(fn: (locale: AppLocale) => void) {
  languageListener = fn;
}

/** Makes `locale` the screen language: loads its text, then sets `<html lang>` and tells listeners. */
export async function setLanguage(locale: AppLocale): Promise<void> {
  const t = await loaders[locale]();
  useI18n.setState({ locale, t });
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  languageListener?.(locale);
}

/** Screen text for components. */
export const useT = () => useI18n((s) => s.t);
/** The language on screen, for formatting dates and numbers. */
export const useLang = () => useI18n((s) => s.locale);
/** Screen text outside components (toasts, error helpers). */
export const tNow = () => useI18n.getState().t;

/**
 * A fixed English message from the server or shared validation, in the screen language: its exact
 * translation, else a general one for its error code, else the message as it came (PRD 11.5).
 */
export function translateMessage(message: string, code?: string): string {
  const t = tNow();
  return t.messages[message] ?? (code ? t.apiCodes[code] : undefined) ?? message;
}

/** BCP 47 tag for `Intl` and `toLocale…String`: English keeps the browser's own English. */
const intlTag = (locale: AppLocale) => (locale === 'en' ? undefined : locale);

export const formatDateTime = (ms: number, locale: AppLocale) => new Date(ms).toLocaleString(intlTag(locale));
export const formatDay = (ms: number, locale: AppLocale, opts?: Intl.DateTimeFormatOptions) =>
  new Date(ms).toLocaleDateString(intlTag(locale), opts);

/**
 * Lower-case, Latin accents removed: "José" and "jose" match. Only Latin combining accents go, so the vowel
 * signs of Gujarati, Devanagari and Gurmukhi (also combining marks) stay part of the word.
 *
 * Arabic (PRD 11.5): vowel marks and the tatwīl stretch are dropped, and letters people write either way match:
 * alef with or without hamza or madda (أ إ آ ٱ ا, which NFD splits into alef and a mark), tāʾ marbūṭa and hāʾ
 * (ة ه), alif maqṣūra and yāʾ (ى ي). So "أحمد" finds "احمد" and "فاطمة" finds "فاطمه".
 */
export const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]|[\u064b-\u065f]|\u0670|\u0640/g, '')
    .replace(/\u0671/g, '\u0627')
    .replace(/\u0629/g, '\u0647')
    .replace(/\u0649/g, '\u064a')
    .toLowerCase();

/** Name order for the screen language (accents sort with their letter). */
export const compareNames = (locale: AppLocale) => new Intl.Collator(intlTag(locale) ?? 'en', { sensitivity: 'base' }).compare;
